/**
 * Layer 1 — Primitive tokens (raw scale).
 * Brand blues extracted from existing theme.ts / global.ts — do not invent palette.
 */
export const primitives = {
  color: {
    brand: {
      50: '#E6F0FF',
      100: '#CCE0FF',
      200: '#99C2FF',
      300: '#66A3FF',
      400: '#3385FF',
      500: '#0066CC',
      600: '#0056B3',
      700: '#004D99',
      800: '#003D7A',
      900: '#00264D',
    },
    neutral: {
      0: '#FFFFFF',
      50: '#F7F7F7',
      100: '#EBEBEB',
      150: '#DDDDDD',
      200: '#C2C2C2',
      300: '#B0B0B0',
      400: '#717171',
      500: '#585858',
      600: '#3D3D3D',
      700: '#2D2D2D',
      800: '#1A1A1A',
      900: '#111111',
      1000: '#000000',
    },
    /** Page background from legacy theme — preserved */
    pageBg: '#F5F7FA',
    /** Legacy text / gray from theme */
    textLegacy: '#333333',
    textMutedLegacy: '#6C757D',
    /** Footer surface used in codebase */
    footer: '#2F3B4C',
    green: {
      50: '#F0FFF4',
      100: '#C6F6D5',
      500: '#28A745',
      700: '#276749',
      900: '#1C4532',
    },
    red: {
      50: '#FFF5F5',
      100: '#FED7D7',
      500: '#DC3545',
      700: '#C53030',
      900: '#742A2A',
    },
    amber: {
      50: '#FFFBEB',
      100: '#FEF3C7',
      500: '#FFC107',
      700: '#B45309',
      900: '#78350F',
    },
  },
  space: {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',
    10: '40px',
    12: '48px',
    14: '56px',
    16: '64px',
    20: '80px',
    24: '96px',
    32: '128px',
  },
  fontSize: {
    xs: '11px',
    sm: '13px',
    base: '15px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
    '6xl': '60px',
    '7xl': '72px',
  },
  fluidFontSize: {
    body: 'clamp(14px, 1.5vw, 16px)',
    h1: 'clamp(30px, 4.2vw, 52px)',
    h2: 'clamp(22px, 3vw, 38px)',
    h3: 'clamp(18px, 2.5vw, 28px)',
    /**
     * Trip itinerary day accordion triggers — one step above body copy, clearly below section h2.
     * (Body in programme blocks is typically `fontSize.sm`; this keeps a ~1.2–1.35× ratio.)
     */
    itineraryDayTitle: 'clamp(15px, 1.2vw + 0.35rem, 17px)',
    /** Marketing hero headline — strong contrast vs body */
    hero: 'clamp(2.25rem, 5.5vw + 0.5rem, 4.5rem)',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeight: {
    tight: 1.2,
    snug: 1.35,
    normal: 1.5,
    relaxed: 1.65,
  },
  letterSpacing: {
    tighter: '-0.04em',
    tight: '-0.02em',
    normal: '0em',
    wide: '0.02em',
    widest: '0.08em',
  },
  radius: {
    none: '0px',
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
    full: '9999px',
  },
  shadow: {
    none: 'none',
    xs: '0 1px 2px rgba(0,0,0,0.04)',
    sm: '0 2px 4px rgba(0,0,0,0.08)',
    md: '0 4px 12px rgba(0,0,0,0.10)',
    lg: '0 8px 24px rgba(0,0,0,0.12)',
    xl: '0 16px 40px rgba(0,0,0,0.14)',
    '2xl': '0 24px 64px rgba(0,0,0,0.18)',
    card: '0 2px 4px rgba(0,0,0,0.04), 0 4px 8px rgba(0,0,0,0.04)',
    cardHover: '0 4px 12px rgba(0,0,0,0.10), 0 8px 24px rgba(0,0,0,0.08)',
    /** Left-edge elevation for right-side mobile drawer */
    drawer: '-10px 0 36px rgba(0,0,0,0.12), -4px 0 12px rgba(0,0,0,0.06)',
  },
  breakpoint: {
    xs: '375px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1440px',
  },
  duration: {
    instant: '0ms',
    fast: '120ms',
    normal: '200ms',
    slow: '300ms',
    lazy: '500ms',
  },
  easing: {
    linear: 'linear',
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
} as const;

export type Primitives = typeof primitives;
